import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { pageMeta } from "@mubin/contracts";
import type { QueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ApiError } from "./lib/api";
import { meQuery } from "./lib/session";
import { LoginPage } from "./routes/login";
import { DownloadPage } from "./routes/download";
import { AndroidTestPage } from "./routes/android-test";
import { AppReleasePage } from "./routes/app-release";
import { PrivacyPage, TermsPage } from "./routes/legal";
import { AccountDeletionPage } from "./routes/account-deletion";
import { DeletionRequestsPage } from "./routes/deletion-requests";
import { Shell } from "./routes/shell";
import { HomePage } from "./routes/home";
import { BranchesPage } from "./routes/branches";
import { CirclesPage } from "./routes/circles";
import { CirclePage } from "./routes/circle";
import { UsersPage } from "./routes/users";
import { UserPage } from "./routes/user";
import { JoinRequestsPage } from "./routes/join-requests";
import { NewStudentsPage } from "./routes/new-students";
import { VolunteersPage } from "./routes/volunteers";
import { ProgramsPage } from "./routes/programs";
import { AuditPage } from "./routes/audit";
import { ReportsPage } from "./routes/reports";
import { SettingsPage } from "./routes/settings";
import { RolesPage } from "./routes/roles";
import { VolunteerPage } from "./routes/volunteer";
import { PoliciesPage } from "./routes/policies";
import { PlanPreviewPage } from "./routes/plan-preview";
import { AttendancePage } from "./routes/attendance";
import { RecitationPage } from "./routes/recitation";
import { ExamsPage } from "./routes/exams";
import { PlansPage } from "./routes/plans";
import { NotifyPage } from "./routes/notify";
import { InterviewBookPage } from "./routes/interview-book";
import { InterviewRoomPage } from "./routes/interview-room";
import { InterviewsPage } from "./routes/interviews";
import { InterviewSlotsPage } from "./routes/interview-slots";

interface Ctx {
  queryClient: QueryClient;
}

/** Every page sets the browser title to its own name (the server writes the same into the served HTML). */
function Root() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    document.title = pageMeta(path).title;
  }, [path]);
  return <Outlet />;
}

const root = createRootRouteWithContext<Ctx>()({ component: Root });

const login = createRoute({ getParentRoute: () => root, path: "/login", component: LoginPage });
// Joining happens in the app (owner decision 2026-10-21): the old join link leads to the download page.
const join = createRoute({ getParentRoute: () => root, path: "/join", component: DownloadPage });
const download = createRoute({ getParentRoute: () => root, path: "/download", component: DownloadPage });
const androidTest = createRoute({
  getParentRoute: () => root,
  path: "/android-test",
  component: AndroidTestPage,
});
const volunteer = createRoute({ getParentRoute: () => root, path: "/volunteer", component: VolunteerPage });
// The applicant's personal interview links (no account; the token is in the URL fragment).
const interviewBook = createRoute({
  getParentRoute: () => root,
  path: "/interview/book",
  component: InterviewBookPage,
});
const interviewRoom = createRoute({
  getParentRoute: () => root,
  path: "/interview/room",
  component: InterviewRoomPage,
});
const privacy = createRoute({ getParentRoute: () => root, path: "/privacy", component: PrivacyPage });
const terms = createRoute({ getParentRoute: () => root, path: "/terms", component: TermsPage });
const accountDeletion = createRoute({
  getParentRoute: () => root,
  path: "/account-deletion",
  component: AccountDeletionPage,
});

const app = createRoute({
  getParentRoute: () => root,
  id: "app",
  component: Shell,
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) throw redirect({ to: "/login" });
      throw e;
    }
  },
});

const home = createRoute({ getParentRoute: () => app, path: "/", component: HomePage });
const branches = createRoute({ getParentRoute: () => app, path: "/branches", component: BranchesPage });
const circles = createRoute({
  getParentRoute: () => app,
  path: "/circles",
  component: CirclesPage,
  validateSearch: z.object({ branchId: z.string().optional(), archived: z.boolean().optional() }),
});
const circle = createRoute({ getParentRoute: () => app, path: "/circles/$circleId", component: CirclePage });
const users = createRoute({
  getParentRoute: () => app,
  path: "/users",
  component: UsersPage,
  validateSearch: z.object({
    tab: z.enum(["student", "teacher", "staff", "former"]).optional(),
    q: z.string().optional(),
    status: z.enum(["active", "suspended"]).optional(),
  }),
});
const user = createRoute({ getParentRoute: () => app, path: "/users/$userId", component: UserPage });
const joinRequests = createRoute({
  getParentRoute: () => app,
  path: "/join-requests",
  component: JoinRequestsPage,
});
const newStudents = createRoute({
  getParentRoute: () => app,
  path: "/new-students",
  component: NewStudentsPage,
});
const volunteers = createRoute({ getParentRoute: () => app, path: "/volunteers", component: VolunteersPage });
const interviews = createRoute({ getParentRoute: () => app, path: "/interviews", component: InterviewsPage });
const interviewSlots = createRoute({
  getParentRoute: () => app,
  path: "/interview-slots",
  component: InterviewSlotsPage,
});
const deletionRequests = createRoute({
  getParentRoute: () => app,
  path: "/deletion-requests",
  component: DeletionRequestsPage,
});
const programs = createRoute({ getParentRoute: () => app, path: "/programs", component: ProgramsPage });
const auditLog = createRoute({ getParentRoute: () => app, path: "/audit", component: AuditPage });
const policies = createRoute({ getParentRoute: () => app, path: "/policies", component: PoliciesPage });
const planPreview = createRoute({
  getParentRoute: () => app,
  path: "/plan-preview",
  component: PlanPreviewPage,
  validateSearch: z.object({ enrollmentId: z.string() }),
});
const reports = createRoute({ getParentRoute: () => app, path: "/reports", component: ReportsPage });
const attendance = createRoute({ getParentRoute: () => app, path: "/attendance", component: AttendancePage });
const plans = createRoute({ getParentRoute: () => app, path: "/plans", component: PlansPage });
const notify = createRoute({ getParentRoute: () => app, path: "/notify", component: NotifyPage });
const exams = createRoute({ getParentRoute: () => app, path: "/exams", component: ExamsPage });
const recitation = createRoute({ getParentRoute: () => app, path: "/recitation", component: RecitationPage });
const settings = createRoute({ getParentRoute: () => app, path: "/settings", component: SettingsPage });
const roles = createRoute({ getParentRoute: () => app, path: "/roles", component: RolesPage });
const appRelease = createRoute({
  getParentRoute: () => app,
  path: "/app-release",
  component: AppReleasePage,
});

export const router = createRouter({
  routeTree: root.addChildren([
    login,
    join,
    download,
    volunteer,
    interviewBook,
    interviewRoom,
    androidTest,
    privacy,
    terms,
    accountDeletion,
    app.addChildren([
      home,
      branches,
      circles,
      circle,
      reports,
      users,
      user,
      joinRequests,
      newStudents,
      appRelease,
      volunteers,
      interviews,
      interviewSlots,
      deletionRequests,
      programs,
      auditLog,
      policies,
      planPreview,
      settings,
      roles,
      attendance,
      recitation,
      exams,
      plans,
      notify,
    ]),
  ]),
  context: { queryClient: undefined! },
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
